import { callBackend } from '../utils/api';

/**
 * Watchlist news service.
 *
 * The current client uses two canonical-identity stages: lightweight
 * watchlist metadata followed by on-demand article bodies for one Cognive
 * identity. The full feed wrapper remains available during rollout.
 *
 *   {
 *     "as_of": ISO-8601 UTC,
 *     "mover_threshold_pct": number,
 *     "items": [{
 *       "cognive_id", "yf_symbol", "display_name",
 *       "return_pct", "is_big_mover",
 *       "articles": [{"article_id", "title", "publisher", "published_at",
 *                    "url", "yahoo_url", "summary"}]
 *     }],
 *     "diagnostics": [...]
 *   }
 *
 * Both `url` (canonical) and `yahoo_url` (clickthrough) are always present per
 * OQ-5 (Trevor 2026-07-27). The client compares them and only surfaces the
 * "Open on Yahoo Finance" affordance when they differ.
 *
 * W6 (2026-08-06) adds `runDeepDive` — the Layer 2 BYOK LLM call. Contract:
 *   Request (POST /api/news/deep_dive):
 *     { cognive_id, provider, model, api_key, force_refresh }
 *   Response:
 *     {
 *       content_md: string,
 *       citations: Array<{url, title, source}>,
 *       cached: boolean,
 *       created_at: ISO-8601 UTC,
 *       provider: string,
 *       model: string,
 *       prompt_version: string,
 *       usage: { input_tokens, output_tokens, ... }
 *     }
 *   Errors: `runDeepDive` throws a `DeepDiveApiError` whose `.status` mirrors
 *   the HTTP status code the route returned (401/403/400/429/504/500/502/…)
 *   so the UI can map to the right snackbar / dialog transition without
 *   string-matching. See `DeepDiveApiError` below.
 */

export const DEFAULT_ARTICLES_PER_SECURITY = 5;
export const DEFAULT_METADATA_RECENT_ARTICLES = DEFAULT_ARTICLES_PER_SECURITY;
export const MAX_ARTICLES_PER_SECURITY = 10;
export const MIN_ARTICLES_PER_SECURITY = 1;

function clampArticlesPerSecurity(raw) {
    const value = Number(raw);
    if (!Number.isFinite(value)) return DEFAULT_ARTICLES_PER_SECURITY;
    const truncated = Math.trunc(value);
    if (truncated < MIN_ARTICLES_PER_SECURITY) return MIN_ARTICLES_PER_SECURITY;
    if (truncated > MAX_ARTICLES_PER_SECURITY) return MAX_ARTICLES_PER_SECURITY;
    return truncated;
}

export async function fetchWatchlistNewsFeed(articlesPerSecurity = DEFAULT_ARTICLES_PER_SECURITY) {
    const clamped = clampArticlesPerSecurity(articlesPerSecurity);
    const endpoint = `api/news/watchlist_feed?articles_per_security=${clamped}`;
    return callBackend(endpoint, 'GET');
}

export async function fetchWatchlistNewsMetadata(
    recentArticlesPerSecurity = DEFAULT_METADATA_RECENT_ARTICLES,
) {
    const clamped = clampArticlesPerSecurity(recentArticlesPerSecurity);
    const endpoint = `api/news/watchlist_metadata?recent_articles_per_security=${clamped}`;
    return callBackend(endpoint, 'GET');
}

export async function fetchSecurityArticles(
    cogniveId,
    limit = DEFAULT_ARTICLES_PER_SECURITY,
) {
    if (typeof cogniveId !== 'string' || !cogniveId.trim()) {
        throw new Error('fetchSecurityArticles: cogniveId must be a non-empty string');
    }
    const clampedLimit = clampArticlesPerSecurity(limit);
    const endpoint = `api/news/security/${encodeURIComponent(cogniveId.trim())}/articles?limit=${clampedLimit}`;
    return callBackend(endpoint, 'GET');
}

/** Fetch the backend-owned Deep Dive provider/model catalog. */
export async function fetchDeepDiveModelCatalog() {
    return callBackend('api/news/deep_dive/models', 'GET');
}

/**
 * Typed error thrown by `runDeepDive`.
 *
 * `callBackend` supplies structured status and detail. Older callers still use
 * the status/body message format; accept both here so the UI's
 * error-mapping switch (401 -> reopen settings, 429 -> "try again shortly",
 * etc.) can key on `err.status` instead of grepping the message.
 *
 * `detail` carries the parsed server message when the response body was
 * JSON with a `detail` field (FastAPI's default HTTPException envelope);
 * otherwise it holds the raw body text.
 *
 * `.status === null` means the failure happened before we ever got an HTTP
 * response (network fault, aborted request, etc.).
 */
export class DeepDiveApiError extends Error {
    constructor(message, { status = null, detail = null } = {}) {
        super(message);
        this.name = 'DeepDiveApiError';
        this.status = status;
        this.detail = detail;
    }
}

// Format `callBackend` throws when a fetch response is `!ok`:
//   "Cloud Run call failed: 401 - {\"detail\":\"..\"}"
// We match generously so the parser survives future changes to that prefix.
const CALL_BACKEND_STATUS_RE = /:\s*(\d{3})\s*-\s*([\s\S]*)$/;

function parseCallBackendError(err) {
    if (err?.name === 'BackendHttpError') {
        return { status: err.status, detail: err.message };
    }
    const rawMessage = err && err.message ? String(err.message) : '';
    const match = rawMessage.match(CALL_BACKEND_STATUS_RE);
    if (!match) {
        return { status: null, detail: rawMessage || 'Deep dive request failed.' };
    }
    const status = Number(match[1]);
    const body = (match[2] || '').trim();
    let detail = body;
    if (body) {
        try {
            const parsed = JSON.parse(body);
            if (parsed && typeof parsed === 'object' && typeof parsed.detail === 'string') {
                detail = parsed.detail;
            }
        } catch (_err) {
            // Non-JSON body — keep the raw text.
        }
    }
    return {
        status: Number.isFinite(status) ? status : null,
        detail: detail || 'Deep dive request failed.',
    };
}

/**
 * Fire the Layer 2 deep-dive request against the alphamizer backend.
 *
 * Params (all required except `force_refresh` and `focus_areas`):
 *   - `cognive_id`  : watchlist security identity.
 *   - `provider`    : 'anthropic' | 'openai' (allowlist enforced backend-side).
 *   - `model`       : provider-specific model id (allowlist enforced backend-side).
 *   - `api_key`     : the user's own API key. Passed as the request body's
 *                     `api_key` field — never as a query string, header, or log
 *                     field. This is the only place in the frontend that the
 *                     raw key travels out of the localStorage wrapper.
 *   - `force_refresh`: skip the server cache and re-invoke the provider.
 *   - `focus_areas`  : array of approved focus-area slugs from the user's
 *                     global preference (or `['all']`). Backend normalizes +
 *                     validates via `normalize_focus_key`.
 *
 * Deep-dive calls take 30-90s end-to-end (provider web search + drafting).
 * The deep-dive dialog displays its own progress while the request runs.
 * We deliberately do NOT add a client-side abort timeout: cutting the request
 * short would waste the user's provider spend that the backend cache would
 * otherwise capture on completion.
 *
 * On failure, throws `DeepDiveApiError` with `.status` = HTTP code (or `null`
 * for network faults). The UI switches on `.status` for error mapping.
 */
export async function runDeepDive({
    cognive_id: cogniveId,
    provider,
    model,
    api_key: apiKey,
    force_refresh: forceRefresh = false,
    focus_areas: focusAreas = null,
} = {}) {
    if (!cogniveId || typeof cogniveId !== 'string') {
        throw new DeepDiveApiError('cognive_id is required.', { status: 400 });
    }
    if (!provider || typeof provider !== 'string') {
        throw new DeepDiveApiError('provider is required.', { status: 400 });
    }
    if (!model || typeof model !== 'string') {
        throw new DeepDiveApiError('model is required.', { status: 400 });
    }
    if (!apiKey || typeof apiKey !== 'string') {
        throw new DeepDiveApiError('api_key is required.', { status: 400 });
    }
    const body = {
        cognive_id: cogniveId,
        provider,
        model,
        api_key: apiKey,
        force_refresh: Boolean(forceRefresh),
        focus_areas: Array.isArray(focusAreas) && focusAreas.length > 0
            ? focusAreas
            : ['all'],
    };
    try {
        return await callBackend('api/news/deep_dive', 'POST', body);
    } catch (err) {
        const { status, detail } = parseCallBackendError(err);
        throw new DeepDiveApiError(detail, { status, detail });
    }
}
